import { compressImage, tooLargeRaw, tooLargeCompressed, fileExt, LONG_CACHE, PHOTO_MAX_SIDE } from "@/lib/image-compress";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Upload, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CitySelect } from "@/components/CitySelect";
import {
  DEAL_TYPES,
  DEAL_TYPE_LABEL,
  ALL_CATEGORIES,
  CONDITION_OPTIONS,
  CURRENCY_OPTIONS,
  CONTACT_PREFERENCES,
  looksLikeService,
  type DealType,
  type ContactPreference,
} from "@/lib/marketplace-constants";

export const Route = createFileRoute("/_authenticated/marketplace/new")({
  component: NewListingPage,
});

function NewListingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const [dealType, setDealType] = useState<DealType>("продам");
  const [category, setCategory] = useState<string>("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [condition, setCondition] = useState<string>("");
  const [deliveryAvailable, setDeliveryAvailable] = useState(false);

  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState<string>("TRY");
  const [priceNegotiable, setPriceNegotiable] = useState(false);
  const [wantInExchange, setWantInExchange] = useState("");
  const [budget, setBudget] = useState("");

  const [contactPreference, setContactPreference] = useState<ContactPreference>("internal");
  const [contactValue, setContactValue] = useState("");

  const serviceHint = useMemo(
    () => looksLikeService(`${title} ${description}`),
    [title, description],
  );

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length || !user) return;
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of files) {
        if (!file.type.startsWith("image/")) continue;
        if (tooLargeRaw(file.size)) {
          toast.error(`${file.name}: больше 5 МБ`);
          continue;
        }
        const upFile = await compressImage(file, { maxSide: PHOTO_MAX_SIDE });
        if (tooLargeCompressed(upFile.size, 5)) {
          toast.error(`${file.name}: больше 5 МБ`);
          continue;
        }
        const ext = fileExt(upFile);
        const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error } = await supabase.storage
          .from("marketplace")
          .upload(path, upFile, { upsert: false, contentType: upFile.type, cacheControl: LONG_CACHE });
        if (error) throw error;
        const { data } = supabase.storage.from("marketplace").getPublicUrl(path);
        uploaded.push(data.publicUrl);
      }
      setPhotos((p) => [...p, ...uploaded].slice(0, 8));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Не удалось загрузить");
    } finally {
      setUploading(false);
    }
  }

  const create = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Unauthorized");
      if (!title.trim()) throw new Error("Укажи заголовок");
      if (!city.trim()) throw new Error("Укажи город");
      if (!category) throw new Error("Выбери категорию");
      const payload = {
        user_id: user.id,
        deal_type: dealType,
        category,
        title: title.trim(),
        description: description.trim() || null,
        city: city.trim(),
        district: district.trim() || null,
        photos,
        condition: condition || null,
        delivery_available: deliveryAvailable,
        price: dealType === "продам" && price.trim() ? Number(price) : null,
        currency: dealType === "продам" ? currency : dealType === "ищу" && budget.trim() ? currency : null,
        price_negotiable: dealType === "продам" ? priceNegotiable : null,
        want_in_exchange: dealType === "обменяю" ? wantInExchange.trim() || null : null,
        budget: dealType === "ищу" && budget.trim() ? Number(budget) : null,
        contact_preference: contactPreference,
        contact_value: contactPreference !== "internal" ? contactValue.trim() || null : null,
        status: "active" as const,
      };
      const { data, error } = await supabase
        .from("marketplace_listings")
        .insert(payload)
        .select("id")
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: (id) => {
      toast.success("Объявление опубликовано");
      navigate({ to: "/marketplace/$listingId", params: { listingId: id } });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="min-h-screen bg-white">
      <main className="mx-auto max-w-2xl px-5 py-8 md:py-12">
        <Link
          to="/marketplace"
          className="mb-4 inline-flex items-center gap-1 text-sm text-td-muted hover:text-td-teal"
        >
          <ArrowLeft className="h-4 w-4" /> К Барахолке
        </Link>

        <h1 className="font-display text-2xl font-bold text-td-dark md:text-3xl">
          Новое объявление
        </h1>
        <div className="mt-2 mb-6 text-sm text-td-muted">Шаг {step} из 3</div>

        {step === 1 && (
          <div className="space-y-5">
            <div>
              <Label>Тип сделки</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {DEAL_TYPES.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDealType(d)}
                    className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition ${
                      dealType === d
                        ? "border-td-teal bg-td-teal text-white"
                        : "border-td-line bg-white text-td-dark hover:border-td-teal"
                    }`}
                  >
                    {DEAL_TYPE_LABEL[d]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Категория</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Выбери категорию" />
                </SelectTrigger>
                <SelectContent>
                  {ALL_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-xl border border-td-line bg-td-soft/40 p-3 text-xs text-td-muted">
              Барахолка предназначена для вещей. <Link to="/profile" search={{ section: "services" }} className="underline underline-offset-2">Для услуг заполни блок «Мои услуги» в профиле — получишь роль «Эксперт»</Link>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => setStep(2)}
                disabled={!category}
                className="bg-td-teal text-white hover:bg-td-teal/90"
              >
                Далее
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div>
              <Label htmlFor="title">Заголовок</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder="Например: Диван IKEA в отличном состоянии"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="description">Описание</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={2000}
                rows={5}
                className="mt-2"
              />
              {serviceHint && (
                <p className="mt-1.5 text-xs text-amber-700">
                  Похоже, ты предлагаешь услугу. <Link to="/profile" search={{ section: "services" }} className="underline underline-offset-2">Для услуг заполни блок «Мои услуги» в профиле — получишь роль «Эксперт»</Link>
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="city">Город</Label>
              <CitySelect id="city" value={city} onChange={setCity} className="mt-2" />
            </div>

            <div>
              <Label htmlFor="district">Район (необязательно)</Label>
              <Input
                id="district"
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                maxLength={80}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Фото (до 8)</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {photos.map((url) => (
                  <div key={url} className="relative h-20 w-20 overflow-hidden rounded-lg border border-td-line">
                    <img loading="lazy" decoding="async" src={url} alt="" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setPhotos((p) => p.filter((x) => x !== url))}
                      className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {photos.length < 8 && (
                  <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-lg border border-dashed border-td-line text-td-muted hover:border-td-teal hover:text-td-teal">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={handleFiles}
                      disabled={uploading}
                    />
                    <Upload className="h-5 w-5" />
                  </label>
                )}
              </div>
              {uploading && <p className="mt-1 text-xs text-td-muted">Загружаем…</p>}
            </div>

            <div>
              <Label>Состояние</Label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Не указано" />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={deliveryAvailable}
                onCheckedChange={(v) => setDeliveryAvailable(!!v)}
              />
              Возможна доставка
            </label>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Назад</Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!title.trim() || !city.trim()}
                className="bg-td-teal text-white hover:bg-td-teal/90"
              >
                Далее
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            {dealType === "продам" && (
              <>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label htmlFor="price">Цена</Label>
                    <Input
                      id="price"
                      type="number"
                      inputMode="numeric"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div className="w-28">
                    <Label>Валюта</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={priceNegotiable}
                    onCheckedChange={(v) => setPriceNegotiable(!!v)}
                  />
                  Торг уместен
                </label>
              </>
            )}

            {dealType === "отдам" && (
              <div className="rounded-xl border border-td-line bg-td-soft/40 p-4 text-sm text-td-dark">
                Отдаёшь бесплатно — дополнительных полей не нужно.
              </div>
            )}

            {dealType === "обменяю" && (
              <div>
                <Label htmlFor="want">Что хочу взамен</Label>
                <Textarea
                  id="want"
                  value={wantInExchange}
                  onChange={(e) => setWantInExchange(e.target.value)}
                  rows={3}
                  className="mt-2"
                  placeholder="Какую вещь готов взять в обмен"
                />
              </div>
            )}

            {dealType === "ищу" && (
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label htmlFor="budget">Бюджет (необязательно)</Label>
                  <Input
                    id="budget"
                    type="number"
                    inputMode="numeric"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    className="mt-2"
                  />
                </div>
                <div className="w-28">
                  <Label>Валюта</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div>
              <Label>Как удобнее связаться</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CONTACT_PREFERENCES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setContactPreference(c)}
                    className={`rounded-full border px-4 py-1.5 text-sm font-semibold capitalize transition ${
                      contactPreference === c
                        ? "border-td-teal bg-td-teal text-white"
                        : "border-td-line bg-white text-td-dark hover:border-td-teal"
                    }`}
                  >
                    {c === "internal" ? "Внутри TurkeyDrive" : c}
                  </button>
                ))}
              </div>
              {contactPreference !== "internal" && (
                <Input
                  className="mt-2"
                  placeholder={contactPreference === "telegram" ? "@username" : "+90..."}
                  value={contactValue}
                  onChange={(e) => setContactValue(e.target.value)}
                />
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>Назад</Button>
              <Button
                onClick={() => create.mutate()}
                disabled={create.isPending}
                className="bg-td-teal text-white hover:bg-td-teal/90"
              >
                {create.isPending ? "Публикуем…" : "Опубликовать"}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
